/* ==========================================================================
   STEEL & SADDLE, CART
   AJAX add / change / remove against Shopify's Cart API, with the drawer
   and the header count kept in sync. Falls back to normal form posts if
   anything goes wrong, so purchasing never becomes impossible.
   ========================================================================== */

window.SS = window.SS || {};

/* Declared up here because the handlers below reference it. */
const routesCartUrl = () => ((window.SSConfig && window.SSConfig.routes && window.SSConfig.routes.cart_url) || '/cart');

SS.cart = (() => {
  const routes = () => (window.SSConfig && window.SSConfig.routes) || {};

  const request = async (url, body) => {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw Object.assign(
        new Error(data.description || data.message || 'We could not update your saddlebag. Try again.'),
        { data }
      );
    }
    return data;
  };

  /* Update the badge straight from the Cart API payload, no round trip. */
  const paintCount = (count) => {
    document.querySelectorAll('[data-cart-count]').forEach((node) => {
      node.textContent = count;
      node.hidden = count === 0;
    });
    /* The badge sits inside <button aria-label="Open saddlebag">, and an
       aria-label overrides descendant text, so the number was invisible to a
       screen reader before and after every update. Rewrite the label. */
    document.querySelectorAll('[data-cart-toggle]').forEach((btn) => {
      btn.setAttribute('aria-label', count === 1 ? 'Open saddlebag, 1 item' : `Open saddlebag, ${count} items`);
    });
  };

  /* One polite live region for the whole cart. Adding, changing a quantity
     and removing a line were all completely silent. */
  const announce = (message) => {
    let region = document.getElementById('cart-live');
    if (!region) {
      region = document.createElement('p');
      region.id = 'cart-live';
      region.className = 'visually-hidden';
      region.setAttribute('aria-live', 'polite');
      region.setAttribute('role', 'status');
      document.body.appendChild(region);
    }
    region.textContent = message;
  };

  /* Swap fresh drawer markup into the DOM. Returns whether it worked, because
     the caller has to know, showing a stale drawer is worse than not opening
     one at all. */
  const paintDrawer = (html) => {
    if (!html) return false;
    const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('#cart-drawer');
    const target = document.querySelector('#cart-drawer');
    if (!fresh || !target) return false;

    // Preserve open state, the drawer is often already visible during this
    const wasOpen = target.classList.contains('is-open');
    fresh.hidden = wasOpen ? false : target.hidden;
    if (wasOpen) fresh.classList.add('is-open');
    fresh.setAttribute('aria-hidden', wasOpen ? 'false' : 'true');
    target.replaceWith(fresh);

    // The overlay controller is still holding the node we just detached. If
    // the drawer was open, hand it the replacement, otherwise its close
    // button operates on an element nobody can see and the drawer appears
    // stuck open.
    if (wasOpen && SS.overlay && SS.overlay.retarget) SS.overlay.retarget('cart-drawer');

    return true;
  };

  /* Re-render the drawer from the server so its markup stays in Liquid rather
     than being duplicated in JS.

     The Cart API returns rendered sections directly when you pass `sections`
     in the POST body, which is one round trip instead of two. The Section
     Rendering API is the fallback.

     IMPORTANT: this must report failure honestly. The original version did
     `if (sections['cart-drawer'])` and silently did nothing when the key was
     missing, which it always was, because the drawer lived in the layout and
     the Section Rendering API cannot address layout-only sections. The drawer
     kept the empty markup it was rendered with at page load, so every single
     add-to-cart appeared to do nothing. Zero checkouts. */
  /* The /cart template's own section id is "main". Without this, a quantity
     change on the cart page repainted only the drawer, so the visible page
     kept the old line prices and the old subtotal while charging the new
     ones. */
  const onCartPage = () => !!document.querySelector('[data-cart-page]');

  /* The real id, published by the section itself. Shopify namespaces section
     ids per template ("template--123456__main"), so neither the template key
     nor a hardcoded "#shopify-section-main" resolves to anything. */
  const cartSectionId = () => {
    const el = document.querySelector('[data-cart-page]');
    return el ? el.getAttribute('data-section-id') : null;
  };

  const paintCartPage = (html) => {
    if (!html) return false;
    const id = cartSectionId();
    const doc = new DOMParser().parseFromString(html, 'text/html');

    /* Shopify sometimes returns the #shopify-section-<id> wrapper and
       sometimes only its contents, so handle both rather than assuming. */
    if (id) {
      const sel = `#shopify-section-${CSS.escape(id)}`;
      const fresh = doc.querySelector(sel);
      const target = document.querySelector(sel);
      if (fresh && target) { target.replaceWith(fresh); return true; }
    }
    const freshInner = doc.querySelector('[data-cart-page]');
    const targetInner = document.querySelector('[data-cart-page]');
    if (freshInner && targetInner) { targetInner.replaceWith(freshInner); return true; }
    return false;
  };

  const refresh = async (sectionsFromCart) => {
    let painted = paintDrawer(sectionsFromCart && sectionsFromCart['cart-drawer']);

    if (onCartPage()) {
      const id = cartSectionId();
      let pagePainted = id ? paintCartPage(sectionsFromCart && sectionsFromCart[id]) : false;
      if (!pagePainted && id) {
        try {
          const res = await fetch(`${routes().cart_url || '/cart'}?sections=${encodeURIComponent(id)}`, { headers: { Accept: 'application/json' } });
          if (res.ok) pagePainted = paintCartPage((await res.json())[id]);
        } catch (err) {
          console.error('Cart page re-render failed', err);
        }
      }
      /* On the cart page the page IS the confirmation, so a repaint there
         counts as success even if the drawer could not be refreshed. */
      painted = painted || pagePainted;
    }

    if (!painted) {
      try {
        const res = await fetch(`${window.location.pathname}?sections=cart-drawer`, {
          headers: { Accept: 'application/json' }
        });
        if (res.ok) {
          const sections = await res.json();
          painted = paintDrawer(sections['cart-drawer']);
        }
      } catch (err) {
        console.error('Cart drawer re-render failed', err);
      }
    }

    // The badge comes straight from the Cart API, so it is right either way.
    try {
      const cartRes = await fetch(`${routes().cart_url || '/cart'}.js`, { headers: { Accept: 'application/json' } });
      const cart = await cartRes.json();
      paintCount(cart.item_count);
      announce(
        cart.item_count === 0
          ? 'Your saddlebag is empty.'
          : `Saddlebag updated. ${cart.item_count} ${cart.item_count === 1 ? 'item' : 'items'}, subtotal ${SS.money(cart.total_price)}.`
      );
      SS.events.emit('cart:updated', cart);
    } catch (err) {
      console.error('Cart count refresh failed', err);
    }

    return painted;
  };

  const add = async (formData) => {
    const body = {
      sections: onCartPage() ? `cart-drawer,${cartSectionId()}` : 'cart-drawer',
      sections_url: onCartPage() ? (routes().cart_url || '/cart') : window.location.pathname
    };
    formData.forEach((value, key) => {
      if (key === 'id') body.id = value;
      else if (key === 'quantity') body.quantity = parseInt(value, 10) || 1;
      else if (key.startsWith('properties[')) {
        body.properties = body.properties || {};
        body.properties[key.slice(11, -1)] = value;
      }
    });
    if (!body.id) throw new Error('Missing variant id');
    const result = await request(`${routes().cart_add_url || '/cart/add'}.js`, body);
    const painted = await refresh(result.sections);
    SS.events.emit('cart:added', result);
    return { result, painted };
  };

  const change = async (key, quantity) => {
    const result = await request(`${routes().cart_change_url || '/cart/change'}.js`, {
      id: key,
      quantity,
      sections: onCartPage() ? `cart-drawer,${cartSectionId()}` : 'cart-drawer',
      sections_url: onCartPage() ? (routes().cart_url || '/cart') : window.location.pathname
    });
    await refresh(result.sections);
    SS.events.emit('cart:changed', result);
    return result;
  };

  return { add, change, refresh };
})();

/* --------------------------------------------------------------------------
   Add-to-cart forms
   -------------------------------------------------------------------------- */
document.addEventListener('submit', async (e) => {
  const form = e.target.closest('[data-cart-form]');
  if (!form) return;

  e.preventDefault();

  const button = form.querySelector('[data-cart-submit]');
  const errorBox = form.querySelector('[data-cart-error]');

  if (button && button.dataset.busy === 'true') return;

  if (errorBox) { errorBox.hidden = true; errorBox.textContent = ''; }
  /* aria-disabled, not disabled. Disabling the element that currently has
     focus moves focus to <body>, which means the drawer had nothing to
     return focus to on close and keyboard users were dumped at the top of
     the document after every add. */
  if (button) {
    button.classList.add('is-loading');
    button.setAttribute('aria-disabled', 'true');
    button.dataset.busy = 'true';
  }

  try {
    const { painted } = await SS.cart.add(new FormData(form));

    if (painted) {
      // Opening the drawer is the confirmation, no toast needed.
      SS.overlay.open('cart-drawer', button);
    } else {
      // The drawer could not be refreshed, so it still shows old contents.
      // Never open it in that state: a drawer that says "your cart is empty"
      // right after someone added something destroys trust instantly. Send
      // them to the real cart page, which is always correct.
      window.location.href = routesCartUrl();
    }
  } catch (err) {
    if (errorBox) {
      errorBox.textContent = (err.data && err.data.description) || err.message || 'Could not add to cart.';
      errorBox.hidden = false;
    } else {
      // No inline error slot on this form, fall back to a real navigation
      form.submit();
      return;
    }
  } finally {
    if (button) {
      button.classList.remove('is-loading');
      button.removeAttribute('aria-disabled');
      delete button.dataset.busy;
    }
  }
});

/* --------------------------------------------------------------------------
   Quantity changes + line removal (drawer and cart page share this)
   -------------------------------------------------------------------------- */
/* --------------------------------------------------------------------------
   Cart drawer cross-sell
   Complementary products for whatever is already in the cart. Filters out
   anything already added, and anything in-store-only, since suggesting a
   product nobody can buy online is worse than suggesting nothing.
   -------------------------------------------------------------------------- */
const loadCartRecs = async () => {
  const zone = document.querySelector('[data-cart-recs]');
  const list = zone && zone.querySelector('[data-cart-recs-list]');
  if (!zone || !list || zone.dataset.loaded === 'true') return;

  try {
    const [recRes, cartRes] = await Promise.all([
      fetch(zone.dataset.url, { headers: { Accept: 'application/json' } }),
      fetch(`${routesCartUrl()}.js`, { headers: { Accept: 'application/json' } })
    ]);
    if (!recRes.ok) return;

    const { products = [] } = await recRes.json();
    const inCart = new Set((await cartRes.json()).items.map((i) => i.product_id));

    const usable = products
      .filter((p) => !inCart.has(p.id) && p.available)
      .slice(0, 2);

    if (!usable.length) return;

    const esc = (v) => String(v == null ? '' : v).replace(/[&<>"']/g, (c) => (
      { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
    ));
    /* v86: product names show with "The" in front, same as snippets/product-name. */
    const theName = (t) => (/^the\s/i.test(String(t || '')) ? t : `The ${t}`);
    list.innerHTML = usable.map((p) => `
      <li class="cartrec__item">
        <a class="cartrec__media" href="${esc(p.url)}" tabindex="-1" aria-hidden="true">
          ${p.featured_image ? `<img src="${esc(p.featured_image)}" alt="" loading="lazy" width="48" height="48">` : ''}
        </a>
        <a class="cartrec__title" href="${esc(p.url)}">${esc(theName(p.title))}</a>
        <span class="cartrec__price">${SS.money(p.price)}</span>
      </li>`).join('');

    zone.hidden = false;
    zone.dataset.loaded = 'true';
  } catch (err) {
    console.error('Cart recommendations failed', err);
  }
};

SS.events.on('cart:updated', () => {
  const zone = document.querySelector('[data-cart-recs]');
  if (zone) zone.dataset.loaded = 'false';
  loadCartRecs();
});
document.addEventListener('DOMContentLoaded', loadCartRecs, { once: true });

const setBusy = (state) => {
  document.querySelectorAll('#cart-drawer .drawer__body, [data-cart-items]').forEach((el) => {
    el.style.opacity = state ? '0.5' : '';
    el.style.pointerEvents = state ? 'none' : '';
    if (state) el.setAttribute('aria-busy', 'true');
    else el.removeAttribute('aria-busy');
  });
};

document.addEventListener('click', async (e) => {
  const remove = e.target.closest('[data-cart-remove]');
  if (remove) {
    e.preventDefault();
    setBusy(true);
    try { await SS.cart.change(remove.getAttribute('data-cart-remove'), 0); }
    catch (err) { console.error(err); window.location.href = routesCartUrl(); }
    finally { setBusy(false); }
    return;
  }

  const step = e.target.closest('[data-qty-step]');
  if (step) {
    e.preventDefault();
    const wrap = step.closest('[data-qty]');
    const input = wrap && wrap.querySelector('input');
    if (!input) return;
    const delta = parseInt(step.getAttribute('data-qty-step'), 10);
    const next = Math.max(parseInt(input.min || '0', 10), (parseInt(input.value, 10) || 0) + delta);
    input.value = next;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }
});

/* Keyed per line. A single module-level timer meant editing two quantities
   in quick succession silently discarded the first edit, and because the
   cart page did not re-render, the input still showed the number that was
   never sent. */
const qtyTimers = new Map();
document.addEventListener('change', (e) => {
  const input = e.target.closest('[data-cart-qty]');
  if (!input) return;

  const key = input.getAttribute('data-cart-qty');
  clearTimeout(qtyTimers.get(key));
  qtyTimers.set(key, setTimeout(async () => {
    qtyTimers.delete(key);
    setBusy(true);
    try { await SS.cart.change(input.getAttribute('data-cart-qty'), parseInt(input.value, 10) || 0); }
    catch (err) { console.error(err); window.location.href = routesCartUrl(); }
    finally { setBusy(false); }
  }, 300));
});

/* --------------------------------------------------------------------------
   QUICK ADD

   One tap from the grid to the saddlebag. Reuses SS.cart.add, so the drawer,
   the badge, the live region and the failure path all behave exactly as they
   do on a product page. Nothing here re-implements cart logic.
   -------------------------------------------------------------------------- */
document.addEventListener('click', async (e) => {
  const btn = e.target.closest('[data-quick-add] .qadd__size');
  if (!btn || btn.disabled) return;
  e.preventDefault();
  e.stopPropagation();          // the whole card is a link; do not follow it

  if (btn.dataset.busy === 'true') return;
  btn.dataset.busy = 'true';
  btn.classList.add('is-busy');

  const fd = new FormData();
  fd.append('id', btn.getAttribute('data-variant'));
  fd.append('quantity', '1');

  try {
    const { painted } = await SS.cart.add(fd);
    if (painted) SS.overlay.open('cart-drawer', btn);
    else window.location.href = routesCartUrl();
  } catch (err) {
    /* No inline error slot on a card, so fall back to the product page
       rather than failing silently in a corner of the grid. */
    const card = btn.closest('.card');
    const link = card && card.querySelector('a[href]');
    if (link) window.location.href = link.href;
  } finally {
    btn.classList.remove('is-busy');
    delete btn.dataset.busy;
  }
});
